// Function to format date from yyyy-mm-dd to dd-mm-yyyy
export const formatDate = (inputDate: string): string => {
  if (!inputDate) {
    return "-"; // Return an empty string if inputDate is undefined or empty
  }

  // Split inputDate into year, month, and day
  const [year, month, day] = inputDate.split("T")?.[0]?.split("-");
  if (!year || !month || !day) {
    return "-";
  }
  // Create a new Date object with yyyy-mm-dd format
  const dateObject = new Date(`${year}-${month}-${day}`);

  // Extract day, month, and year from dateObject
  const formattedDay = ("0" + dateObject.getDate()).slice(-2);
  const formattedMonth = ("0" + (dateObject.getMonth() + 1)).slice(-2);
  const formattedYear = dateObject.getFullYear();

  // Return formatted date in dd-mm-yyyy format
  return `${formattedDay}-${formattedMonth}-${formattedYear}`;
};

// if date format like this "Mon Jul 30 2018 05:30:00 GMT+0530 (India Standard Time)" format to "dd-mm-yyyy"
export const parseAndFormatDate = (inputDate: string): string => {
  if (!inputDate) {
    return ""; // Return an empty string if inputDate is undefined or empty
  }

  const dateObject = new Date(inputDate);

  if (isNaN(dateObject.getTime())) {
    return ""; // Return an empty string if inputDate is not a valid date
  }

  const formattedDay = ("0" + dateObject.getDate()).slice(-2);
  const formattedMonth = ("0" + (dateObject.getMonth() + 1)).slice(-2);
  const formattedYear = dateObject.getFullYear();

  return `${formattedDay}-${formattedMonth}-${formattedYear}`;
};

export const formatNormalDate = (dateString: string) => {
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

// Function to limit a number to two decimal places
export const formatNumberToTwoDecimals = (number: any): string => {
  if (number === null || number === "" || isNaN(number) ) {
    return ""; // Return an empty string if number is null or NaN
  }
  return number.toFixed(2);
};

export const formatDateTime = (isoTime: string) => {
  const hours = isoTime.split(".");
  // const dateObj = new Date(`1970-01-01T${isoTime}`.toString());
  // const hours = String(dateObj.getHours()).padStart(2, '0');
  // const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  // const seconds = String(dateObj.getSeconds()).padStart(2, '0');

  return `${hours[0]}`;
};

/**
 * Formats a date string to a "dd mmm yyyy" format.
 * For example, "2025-04-15" will be formatted to "15 Apr 2025".
 * @param dateString The input date string.
 * @returns The formatted date string.
 */
export const formatDateToShortMonth = (dateString: string): string => {
  if (!dateString) {
    return "";
  }

  const dateObject = new Date(dateString);
  if (isNaN(dateObject.getTime())) {
    return "";
  }

  const day = dateObject.getDate();
  const year = dateObject.getFullYear();

  // Array of short month names
  const shortMonths = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = shortMonths[dateObject.getMonth()];

  return `${day} ${month} ${year}`;
};

// Helper function to format the ISO date string to 12/02/2000
export const formatISOToDDMMYYYY = (isoString: any) => {
  if (!isoString) return "";

  const date = new Date(isoString);

  // Get day (DD) and pad with leading zero if needed
  const day = String(date.getDate()).padStart(2, "0");

  // Get month (MM) - getMonth() is 0-indexed, so add 1, then pad
  const month = String(date.getMonth() + 1).padStart(2, "0");

  // Get full year (YYYY)
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Formats an ISO timestamp to show time elapsed (e.g., "5 mins ago", "Just now").
 * If the message is 24 hours or older, it displays the date in DD-MM-YYYY, H:MM AM/PM format.
 *
 * @param {string} isoString The ISO 8601 timestamp string.
 * @returns {string} The formatted time difference or full date/time string.
 */
export const formatTimeAgo = (isoString: any) => {
  if (!isoString) return "";

  const messageDate = new Date(isoString);
  const now = new Date();

  // Calculate the difference in milliseconds
  const diffMs = now.getTime() - messageDate.getTime();
  const diffMins = Math.round(diffMs / (1000 * 60));

  // Helper to format the date as DD-MM-YYYY
  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper to format the time as H:MM AM/PM
  const formatTimeOnly = (date: Date): string => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // 1. Check if the message is LESS THAN 24 HOURS OLD
  const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

  if (diffMs < ONE_DAY_IN_MS) {
    if (diffMins < 1) {
      return "Just now";
    } else if (diffMins === 1) {
      return "1 min ago";
    } else if (diffMins < 60) {
      return `${diffMins} mins ago`;
    }

    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }

  // 2. If message is 24 HOURS OR OLDER (show date/time format)
  else {
    const formattedDate = formatDate(messageDate);
    const timeOnly = formatTimeOnly(messageDate);

    // Example: 11-12-2025, 4:57 PM
    return `${formattedDate}, ${timeOnly}`;
  }
};
export const getDateRange = (loanEndDateStr: string, loanStartDateStr: string) => {
  const currentDate = new Date();
  const loanEndDate = new Date(loanEndDateStr);

  // Start Date: Current Date - 6 months
  const startDate = new Date(currentDate);
  startDate.setMonth(startDate.getMonth() - 5);
  // Step 2: Loan start date
  const loanStartDate = new Date(loanStartDateStr);

  // Step 3: Pick whichever is later
  const finalStartDate = startDate < loanStartDate ? loanStartDate : startDate;
  // Option 1: Current Date + 5 years
  const datePlus5Years = new Date(currentDate);
  datePlus5Years.setFullYear(datePlus5Years.getFullYear() + 5);

  // End Date: whichever is earlier between datePlus5Years and loanEndDate
  const endDate = loanEndDate < datePlus5Years ? loanEndDate : datePlus5Years;

  // Format date as DD.MM.YYYY
  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  return `(Date range: ${formatDate(finalStartDate)} to ${formatDate(endDate)})`;
};

export const getDateRangeObject = (loanEndDateStr: string,loanStartDateStr: string) => {
  const currentDate = new Date();
  const loanEndDate = new Date(loanEndDateStr);

  // Start Date: Current Date - 6 months
  const startDate = new Date(currentDate);
  startDate.setMonth(startDate.getMonth() - 5);
  //startDate.setDate(startDate.getDate() + 1)
  // Step 2: Loan start date
  const loanStartDate = new Date(loanStartDateStr);

  // Step 3: Pick whichever is later
  const finalStartDate = startDate < loanStartDate ? loanStartDate : startDate;
  // Option 1: Current Date + 5 years
  const datePlus5Years = new Date(currentDate);
  datePlus5Years.setFullYear(datePlus5Years.getFullYear() + 5);

  // End Date: whichever is earlier between datePlus5Years and loanEndDate
  const endDate = loanEndDate < datePlus5Years ? loanEndDate : datePlus5Years;

  // Format date as DD.MM.YYYY
  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
    const year = date.getFullYear();
    return `${year}-${month}-${day}T00:00:00Z`;
  };

  return { startDate: formatDate(finalStartDate), endDate: formatDate(endDate) };
};

export const getDaysBetween = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffTime = endDate.getTime() - startDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
  return diffDays;
};
